/**
 * Dataset aggregation utility
 * Groups submissions into datasets by category + subcategory
 */

export interface Submission {
  id: string;
  submissionId: string;
  userId: string;
  taskId: string;
  taskTitle?: string;
  category?: string;
  subcategory?: string;
  mediaUrl?: string;
  assetUrl?: string;
  labels?: any[];
  labelCount?: number;
  createdAt?: any;
  approvedAt?: any;
  completedAt?: any;
  [key: string]: any; // Allow flexible access to Firestore document properties
}

export interface Dataset {
  datasetId: string; // slug: ${category}_${subcategory}
  category: string;
  subcategory: string;
  totalImages: number;
  totalLabels: number;
  tasksIncluded: string[]; // Array of unique task titles
  lastUpdated: any; // Timestamp of latest approved submission
  thumbnailUrl?: string; // First approved image URL
  submissions: Submission[]; // All submissions in this dataset
}

function hasValidLabels(data: any): boolean {
  const labels = data.labels;
  const hasLabels = labels && 
                   Array.isArray(labels) && 
                   labels.length > 0 &&
                   labels.some((label: any) => label !== null && label !== undefined);
  return hasLabels === true;
}

function deriveLabelCount(data: any): number {
  if (typeof data.labelCount === 'number') {
    return data.labelCount;
  }
  if (Array.isArray(data.labels)) {
    return data.labels.filter((label: any) => label !== null && label !== undefined).length;
  }
  if (typeof data.totalLabels === 'number') {
    return data.totalLabels;
  }
  if (data.meta?.labelCount) {
    return Number(data.meta.labelCount) || 0;
  }
  return 0;
}

function getTimestampValue(timestamp: any): number {
  if (!timestamp) return 0;
  if (timestamp.toMillis) return timestamp.toMillis();
  if (timestamp.seconds) return timestamp.seconds * 1000;
  if (typeof timestamp === 'number') return timestamp;
  return 0;
}

/**
 * Aggregates submissions into datasets grouped by category + subcategory
 * @param submissions Array of submission objects
 * @returns Array of dataset objects
 */
export function aggregateDatasetsFromSubmissions(submissions: Submission[]): Dataset[] {
  // Filter to only submissions with valid labels
  const validSubmissions = submissions.filter((sub: any) => {
    return hasValidLabels(sub);
  });

  // Group by category + subcategory
  const datasetMap = new Map<string, {
    category: string;
    subcategory: string;
    submissions: Submission[];
    taskTitles: Set<string>;
    lastUpdated: any;
    thumbnailUrl?: string;
  }>();

  validSubmissions.forEach((sub: any) => {
    const category = (sub.category || sub.taskCategory || 'General').trim();
    const subcategory = (sub.subcategory || sub.taskSubcategory || 'N/A').trim();
    
    // Create dataset key
    const datasetKey = `${category}_${subcategory}`;

    if (!datasetMap.has(datasetKey)) {
      datasetMap.set(datasetKey, {
        category,
        subcategory,
        submissions: [],
        taskTitles: new Set<string>(),
        lastUpdated: null,
        thumbnailUrl: undefined,
      });
    }

    const dataset = datasetMap.get(datasetKey)!;
    dataset.submissions.push(sub);

    // Add task title if available
    const taskTitle = sub.taskTitle || sub.taskName || sub.task?.title;
    if (taskTitle) {
      dataset.taskTitles.add(taskTitle);
    }

    // Update lastUpdated (most recent timestamp)
    const subTimestamp = sub.completedAt || sub.approvedAt || sub.createdAt;
    const subTimestampValue = getTimestampValue(subTimestamp);
    const currentTimestampValue = getTimestampValue(dataset.lastUpdated);
    
    if (subTimestampValue > currentTimestampValue) {
      dataset.lastUpdated = subTimestamp;
      // Set thumbnail to first image we encounter (or most recent)
      dataset.thumbnailUrl = sub.mediaUrl || sub.assetUrl || dataset.thumbnailUrl;
    } else if (!dataset.thumbnailUrl) {
      // If no thumbnail yet, use this one
      dataset.thumbnailUrl = sub.mediaUrl || sub.assetUrl;
    }
  });

  // Convert map to Dataset array
  const datasets: Dataset[] = Array.from(datasetMap.entries()).map(([datasetId, data]) => {
    const totalLabels = data.submissions.reduce((sum, sub) => {
      return sum + deriveLabelCount(sub);
    }, 0);

    return {
      datasetId,
      category: data.category,
      subcategory: data.subcategory,
      totalImages: data.submissions.length,
      totalLabels,
      tasksIncluded: Array.from(data.taskTitles).sort(),
      lastUpdated: data.lastUpdated,
      thumbnailUrl: data.thumbnailUrl,
      submissions: data.submissions,
    };
  });

  // Sort by lastUpdated descending (most recent first)
  datasets.sort((a, b) => {
    const aTime = getTimestampValue(a.lastUpdated);
    const bTime = getTimestampValue(b.lastUpdated);
    return bTime - aTime;
  });

  return datasets;
}

/**
 * Aggregates submissions into datasets grouped by category only
 * This creates one dataset per category, combining all subcategories
 * @param submissions Array of submission objects
 * @returns Array of dataset objects (one per category)
 */
export function aggregateDatasetsByCategory(submissions: Submission[]): Dataset[] {
  // Filter to only submissions with valid labels
  const validSubmissions = submissions.filter((sub: any) => {
    return hasValidLabels(sub);
  });

  // Group by category only
  const datasetMap = new Map<string, {
    category: string;
    submissions: Submission[];
    taskTitles: Set<string>;
    subcategories: Set<string>;
    lastUpdated: any;
    thumbnailUrl?: string;
  }>();

  validSubmissions.forEach((sub: any) => {
    const category = (sub.category || sub.taskCategory || 'General').trim();
    
    // Use category as the dataset key
    const datasetKey = category;

    if (!datasetMap.has(datasetKey)) {
      datasetMap.set(datasetKey, {
        category,
        submissions: [],
        taskTitles: new Set<string>(),
        subcategories: new Set<string>(),
        lastUpdated: null,
        thumbnailUrl: undefined,
      });
    }

    const dataset = datasetMap.get(datasetKey)!;
    dataset.submissions.push(sub);

    // Add task title if available
    const taskTitle = sub.taskTitle || sub.taskName || sub.task?.title;
    if (taskTitle) {
      dataset.taskTitles.add(taskTitle);
    }

    // Track subcategories
    const subcategory = (sub.subcategory || sub.taskSubcategory || 'N/A').trim();
    if (subcategory && subcategory !== 'N/A') {
      dataset.subcategories.add(subcategory);
    }

    // Update lastUpdated (most recent timestamp)
    const subTimestamp = sub.completedAt || sub.approvedAt || sub.createdAt;
    const subTimestampValue = getTimestampValue(subTimestamp);
    const currentTimestampValue = getTimestampValue(dataset.lastUpdated);
    
    if (subTimestampValue > currentTimestampValue) {
      dataset.lastUpdated = subTimestamp;
      // Set thumbnail to first image we encounter (or most recent)
      dataset.thumbnailUrl = sub.mediaUrl || sub.assetUrl || dataset.thumbnailUrl;
    } else if (!dataset.thumbnailUrl) {
      // If no thumbnail yet, use this one
      dataset.thumbnailUrl = sub.mediaUrl || sub.assetUrl;
    }
  });

  // Convert map to Dataset array
  const datasets: Dataset[] = Array.from(datasetMap.entries()).map(([category, data]) => {
    const totalLabels = data.submissions.reduce((sum, sub) => {
      return sum + deriveLabelCount(sub);
    }, 0);

    // For category-level datasets, use "Complete Set" as subcategory
    // datasetId is just the category name (no underscore)
    return {
      datasetId: category, // Just category, no subcategory
      category: data.category,
      subcategory: 'Complete Set',
      totalImages: data.submissions.length,
      totalLabels,
      tasksIncluded: Array.from(data.taskTitles).sort(),
      lastUpdated: data.lastUpdated,
      thumbnailUrl: data.thumbnailUrl,
      submissions: data.submissions,
    };
  });

  // Sort by lastUpdated descending (most recent first)
  datasets.sort((a, b) => {
    const aTime = getTimestampValue(a.lastUpdated);
    const bTime = getTimestampValue(b.lastUpdated);
    return bTime - aTime;
  });

  return datasets;
}

